import React, { Fragment, useState, useEffect } from "react"
import {
  Container,
  Col,
  Row,
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  Form,
  Label,
} from "reactstrap"
import { useMutation, useQuery } from "react-query"
import { GENERAL } from "../../api/general"
import { useNavigate } from "react-router-dom"
//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import Swal from "sweetalert2"
import PaginatedDataTable from "components/PaginatedDataTable"

const NationalTargets = props => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [allRecords, setAllRecords] = useState([])
  const [filter, setFilter] = useState("")
  const [editingRow, setEditingRow] = useState(null)
  const [editingValue, setEditingValue] = useState("")
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [states, setStates] = useState([])
  const [createStateId, setCreateStateId] = useState("")
  const [createTarget, setCreateTarget] = useState("")

  const getStates = useQuery({
    queryKey: ["getStates"],
    queryFn: () => GENERAL.getStates(),
    onSuccess: async res => {
      if (res?.palliative) setStates(res.palliative)
    },
  })

  const openCreateModal = () => {
    setCreateStateId("")
    setCreateTarget("")
    setShowCreateModal(true)
  }
  const closeCreateModal = () => setShowCreateModal(false)

  const handleCreateTarget = () => {
    if (!createStateId) {
      return Swal.fire("Validation", "State is required.", "warning")
    }
    if (!createTarget && createTarget !== 0) {
      return Swal.fire("Validation", "Target value is required.", "warning")
    }
    Swal.fire({
      title: "Saving target...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(Swal.getDenyButton()),
    })
    updateStateTarget.mutate({
      stateId: createStateId,
      target: String(createTarget),
    })
  }

  // GET /admin/national-targets returns the full state list in one shot (no
  // server-side pagination), so paging/searching is done client-side below.
  const fetchRecords = async () => {
    Swal.fire({
      title: "Fetching records...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(Swal.getDenyButton())
      },
    })
    try {
      const response = await getNationalTargets.mutateAsync()
      Swal.close()
      setAllRecords(response?.stateTargets || [])
    } catch (error) {
      Swal.fire("Error fetching records", error.message, "error")
    }
  }

  useEffect(() => {
    fetchRecords()
  }, [])

  const getNationalTargets = useMutation({
    mutationFn: () => GENERAL.getNationalTargets(),
    onError: error => Swal.fire("Error", error.message, "error"),
  })

  const updateStateTarget = useMutation({
    mutationFn: payload => GENERAL.updateStateTarget(payload),
    onSuccess: res => {
      Swal.fire("Success", "Target updated successfully.", "success").then(() =>
        fetchRecords()
      )
      setEditingRow(null)
      setShowCreateModal(false)
    },
    onError: error => Swal.fire("Error", error.message, "error"),
  })

  const handleSaveTarget = row => {
    if (!editingValue && editingValue !== 0) {
      return Swal.fire("Validation", "Target value is required.", "warning")
    }
    Swal.fire({
      title: "Saving target...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(Swal.getDenyButton()),
    })
    updateStateTarget.mutate({
      stateId: String(row.stateId),
      target: String(editingValue),
    })
  }

  const handlePageChange = page => setPage(page)
  const handlePerRowsChange = newPageSize => {
    setPageSize(newPageSize)
    setPage(1)
  }
  const handleSearchFn = () => setPage(1)

  const filteredRecords = filter
    ? allRecords.filter(row =>
        String(row.stateName).toLowerCase().includes(filter.trim().toLowerCase())
      )
    : allRecords

  const startIndex = (page - 1) * pageSize
  const data = filteredRecords.slice(startIndex, startIndex + pageSize)
  const totalRecords = filteredRecords.length

  const columns = [
    {
      name: "#",
      selector: (row, index) => startIndex + index + 1,
      width: "4rem",
      sortable: true,
    },
    {
      name: "State",
      sortable: true,
      selector: row => row.stateName,
    },
    {
      name: "Current Target",
      sortable: true,
      selector: row =>
        editingRow === row.stateId ? (
          <Input
            type="number"
            min={0}
            value={editingValue}
            onChange={e => setEditingValue(e.target.value)}
            style={{ width: 120 }}
          />
        ) : (
          <span>{row.target ?? "—"}</span>
        ),
    },
    {
      name: "Actual Registrations",
      sortable: true,
      selector: row => row.registrationCount ?? 0,
    },
    {
      name: "% Completion",
      sortable: true,
      selector: row =>
        row.target
          ? `${Math.round(((row.registrationCount ?? 0) / row.target) * 100)}%`
          : "—",
    },
    {
      name: "Action",
      sortable: false,
      selector: row => (
        <div style={{ display: "flex", gap: 6 }}>
          {editingRow === row.stateId ? (
            <>
              <Button
                color="success"
                size="sm"
                onClick={() => handleSaveTarget(row)}
              >
                Save
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={() => setEditingRow(null)}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                color="primary"
                size="sm"
                onClick={() => {
                  setEditingRow(row.stateId)
                  setEditingValue(row.target ?? "")
                }}
              >
                Set Target
              </Button>
              {/* <Button
                color="info"
                size="sm"
                onClick={() =>
                  navigate(`/national-targets-lga/${row.stateId}`, {
                    state: { stateName: row.stateName },
                  })
                }
              >
                View LGAs
              </Button> */}
            </>
          )}
        </div>
      ),
    },
  ]

  document.title = "National Targets | UNICEF - e-Recruitment Portal"

  return (
    <React.Fragment>
      <Modal size="md" isOpen={showCreateModal} centered={true}>
        <ModalBody className="py-3 px-5">
          <Row style={{ paddingTop: "25px", paddingBottom: "25px" }}>
            <Col lg={12} style={{ marginTop: "10px", marginBottom: "10px" }}>
              <h5>SET NATIONAL TARGET</h5>
            </Col>
            <Form>
              <Col md={12}>
                <div className="mb-3">
                  <Label htmlFor="create-target-state">
                    State <span className="text-danger">*</span>
                  </Label>
                  <select
                    id="create-target-state"
                    onChange={e => setCreateStateId(e.target.value)}
                    className="form-control"
                    value={createStateId}
                  >
                    <option value="">Select</option>
                    {states?.map(state => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3">
                  <Label htmlFor="create-target-value">
                    Target <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="create-target-value"
                    type="number"
                    min={0}
                    value={createTarget}
                    onChange={e => setCreateTarget(e.target.value)}
                    placeholder="Enter target number"
                  />
                </div>
              </Col>
            </Form>
          </Row>
          <Row>
            <Col xl={6}>
              <button
                type="submit"
                className="btn btn-info w-100"
                onClick={handleCreateTarget}
              >
                SAVE TARGET
              </button>
            </Col>
            <Col xl={6}>
              <button
                type="submit"
                style={{ marginLeft: 9 }}
                className="btn btn-danger w-100"
                onClick={closeCreateModal}
              >
                CANCEL
              </button>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      <div className="page-content">
        <Container fluid className="container-xxl" style={{ minWidth: "97%" }}>
          <Row>
            <Breadcrumbs
              title={props.t("National Targets")}
              breadcrumbItem={props.t("Manage State Targets")}
              hasBackBtn={false}
            />
          </Row>
          <Card>
            <CardBody>
              <div className="mb-4 h4 card-title">National Registration Targets</div>
              <Fragment>
                <Row className="mb-2">
                  <Col md={8}>
                    <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
                      <div
                        className="position-relative"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <label htmlFor="search-bar-0" className="search-label">
                          <span id="search-bar-0-label" className="sr-only">
                            Search this table
                          </span>
                          <input
                            onChange={e => setFilter(e.target.value)}
                            id="search-bar-0"
                            type="text"
                            className="form-control"
                            placeholder="Search states..."
                            value={filter || ""}
                          />
                        </label>
                        <i
                          className="bx bx-search-alt search-icon"
                          style={{ cursor: "pointer" }}
                          onClick={handleSearchFn}
                        ></i>
                        <div
                          className="d-inline-block"
                          style={{ marginLeft: 5, marginBottom: 8 }}
                        >
                          <Button
                            type="button"
                            color="primary"
                            className="btn-md btn-rounded"
                            onClick={handleSearchFn}
                          >
                            Search
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-sm-end">
                      <Button
                        type="button"
                        color="primary"
                        className="btn mb-2 me-2"
                        onClick={openCreateModal}
                      >
                        Create Target
                      </Button>
                    </div>
                  </Col>
                </Row>
                <PaginatedDataTable
                  columns={columns}
                  data={data}
                  totalRecords={totalRecords}
                  handlePageChange={handlePageChange}
                  handlePerRowsChange={handlePerRowsChange}
                />
              </Fragment>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(NationalTargets)
